import React, { useState, useRef, useEffect, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Sparkles, X, GripVertical } from "lucide-react";
import { useSettings } from "@/lib/settings-context";
import { useEntitlements } from "@/lib/use-entitlements";

/**
 * Ambient FAB (floating-action button) for the Sense AI Builder.
 * The button is intentionally pinned to the bottom-right corner so it
 * stays visible while scrolling and does not drift into the center of the
 * viewport.
 *
 * iter-121.109 — When the FAB is rendered on a dashboard page
 * (/dashboards/<id>), the navigate target carries that dashboard_id as a
 * query param. The Builder backend uses it to inject the LIVE widget list
 * into the LLM prompt — so the AI knows EXACTLY which dashboard the user
 * is editing and stops guessing via list_my_dashboards.
 *
 * 
 *
 * Hidden on:
 *   • /sense-ai (would be self-referential)
 *   • Login / 404 routes (no auth context)
 *
 * Theme-aware: uses `bg-primary text-primary-foreground` so it inverts in
 * dark mode without further work. Keyboard-navigable (focusable button).
 */
const FAB_HIDDEN_PREFIXES = ["/sense-ai", "/login", "/forgot-password", "/reset-password"];
const FAB_W = 84;
const FAB_H = 48;
const POS_KEY = "sense-fab-pos";

function clampToViewport(x, y) {
    const maxX = Math.max(0, (typeof window !== "undefined" ? window.innerWidth : 1024) - FAB_W);
    const maxY = Math.max(0, (typeof window !== "undefined" ? window.innerHeight : 768) - FAB_H);
    return {
        x: Math.min(Math.max(0, x), maxX),
        y: Math.min(Math.max(0, y), maxY),
    };
}

function loadStoredPos() {
    if (typeof window === "undefined") return null;
    try {
        const raw = window.localStorage.getItem(POS_KEY);
        if (!raw) return null;
        const obj = JSON.parse(raw);
        // Only load position if explicitly dragged by user (prevents stale centered defaults)
        if (obj?.userDragged && typeof obj?.x === "number" && typeof obj?.y === "number") {
            return { ...clampToViewport(obj.x, obj.y), userDragged: true };
        }
    } catch (_e) { /* ignore */ }
    return null;
}

// Fixed issue where FAB drifted to center of screen on refresh by defaulting to null (CSS bottom-right anchored)
export default function SenseAIBuilderFAB() {
    const { getAccessLevel } = useSettings();
    const senseAiLevel = getAccessLevel("sense_ai");
    const { entitled, openUpgrade, role } = useEntitlements();
    // The FAB opens the BUILDER → gated on `sense_ai_builder` (separate from
    // Sense AI chat). Not entitled → clicking opens the upgrade popup.
    const builderLocked = role !== "super_admin" && !entitled("sense_ai_builder");
    const navigate = useNavigate();
    const location = useLocation();
    const [dismissed, setDismissed] = useState(false);
    const [pos, setPos] = useState(() => loadStoredPos());
    const [dragging, setDragging] = useState(false);
    const wrapperRef = useRef(null);
    const dragOffset = useRef({ dx: 0, dy: 0 });
    const dragMoved = useRef(false);
    const path = location.pathname || "";

    // Persist position changes to localStorage only if explicitly user-dragged
    useEffect(() => {
        if (typeof window === "undefined") return;
        try {
            if (pos && pos.userDragged) {
                window.localStorage.setItem(POS_KEY, JSON.stringify(pos));
            } else {
                window.localStorage.removeItem(POS_KEY);
            }
        } catch (_e) { /* ignore */ }
    }, [pos]);

    useEffect(() => {
        if (typeof window === "undefined") return;
        const onResize = () => setPos((p) => (p && p.userDragged ? { ...clampToViewport(p.x, p.y), userDragged: true } : null));
        window.addEventListener("resize", onResize);
        return () => window.removeEventListener("resize", onResize);
    }, []);

    const lastClickTimeRef = useRef(0);

    const handleReset = useCallback(() => {
        setPos(null);
        setDragging(false);
        try {
            window.localStorage.removeItem(POS_KEY);
        } catch (_e) { /* ignore */ }
    }, []);

    const onPointerDown = useCallback((e) => {

    // Only the grip-handle triggers drag — Sparkles button & dismiss X remain clickable.

    e.stopPropagation();
 
    // Detect rapid double click (within 350ms) to reset position back to bottom-right corner

    const now = Date.now();

    if (now - lastClickTimeRef.current < 350) {

        handleReset();

        lastClickTimeRef.current = 0;

        return;

    }

    lastClickTimeRef.current = now;
 
    let startX = pos?.x;

    let startY = pos?.y;

    if (startX === undefined || startY === undefined) {

        if (wrapperRef.current) {

            const rect = wrapperRef.current.getBoundingClientRect();

            startX = rect.left;

            startY = rect.top;

        } else {

            startX = (typeof window !== "undefined" ? window.innerWidth : 1024) - FAB_W - 24;

            startY = (typeof window !== "undefined" ? window.innerHeight : 768) - FAB_H - 24;

        }

    }
 
    // Merged: Null-safe startX/startY + developer's start coordinates (sx, sy)

    dragOffset.current = { 

        dx: e.clientX - startX, 

        dy: e.clientY - startY, 

        sx: e.clientX, 

        sy: e.clientY 

    };

    dragMoved.current = false;

    setDragging(true);

    try { e.currentTarget.setPointerCapture?.(e.pointerId); } catch (_e) { /* ignore */ }

}, [pos, handleReset]);
 
 


    const onPointerMove = useCallback((e) => {
        if (!dragging) return;
        const nx = e.clientX - dragOffset.current.dx;
        const ny = e.clientY - dragOffset.current.dy;
        // Only treat as a DRAG once motion exceeds a small threshold (4px), so a
        // normal click with incidental micro-motion still fires onClick.
        const dist = Math.hypot(
            e.clientX - (dragOffset.current.sx ?? e.clientX),
            e.clientY - (dragOffset.current.sy ?? e.clientY)
        );
        if (dist > 4) dragMoved.current = true;
 
        setPos({ ...clampToViewport(nx, ny), userDragged: true });
    }, [dragging]);

    const onPointerUp = useCallback((e) => {
        if (!dragging) return;
        setDragging(false);
        try { e.currentTarget.releasePointerCapture?.(e.pointerId); } catch (_e) { /* ignore */ }
    }, [dragging]);

    if (dismissed) return null;
    if (senseAiLevel === "-") return null;
    if (FAB_HIDDEN_PREFIXES.some((p) => path.startsWith(p))) return null;

    // Extract current dashboard_id or template_id for AI builder context
    let contextQuery = "";
    const dashMatch = path.match(/^\/dashboards\/([0-9a-f-]{8,})/i);
    if (dashMatch) {
        contextQuery = `&dashboard_id=${encodeURIComponent(dashMatch[1])}`;
    } else {
        const galMatch = path.match(/^\/gallery\/preview\/([0-9a-f-]{8,})/i);
        if (galMatch) {
            contextQuery = `&template_id=${encodeURIComponent(galMatch[1])}`;
        }
    }

    const wrapperStyle = pos
        ? { left: `${pos.x}px`, top: `${pos.y}px`, touchAction: "none" }
        : { right: "24px", bottom: "24px", touchAction: "none" };

    return (
        <div
            ref={wrapperRef}
            className="fixed z-40 group pointer-events-none"
            style={wrapperStyle}
            data-testid="sense-ai-builder-fab-wrap">
            {/* Tooltip */}
            <span className="hidden md:block absolute right-14 top-1/2 -translate-y-1/2 bg-foreground text-background text-[11px] font-mono uppercase tracking-wider px-2.5 py-1 rounded-sm opacity-0 group-hover:opacity-100 transition-opacity duration-150 pointer-events-none whitespace-nowrap">
                Drag handle to move · Double-click handle to reset · Click to open Sense AI
            </span>
            <div
                className="flex items-center pointer-events-auto">
                <button
                    type="button"
                    onPointerDown={onPointerDown}
                    onPointerMove={onPointerMove}
                    onPointerUp={onPointerUp}
                    onPointerCancel={onPointerUp}
                    className={`h-12 w-4 rounded-l-full bg-primary/85 text-primary-foreground flex items-center justify-center shadow-lg ${dragging ? "cursor-grabbing" : "cursor-grab"}`}
                    title="Drag to reposition (Double-click to reset to bottom-right)"
                    aria-label="Drag Sense AI bubble"
                    onDoubleClick={(e) => {
                        e.stopPropagation();
                        handleReset();
                    }}
                    data-testid="sense-ai-builder-fab-drag">
                    <GripVertical className="h-3 w-3 opacity-80" />
                </button>
                <button
                    onClick={() => {
                        if (dragMoved.current) {
                            dragMoved.current = false;
                            return;
                        }
                        if (builderLocked) { openUpgrade(); return; }
                        navigate(`/sense-ai?mode=builder${contextQuery}`);
                    }}
                    className="relative h-12 w-12 rounded-r-full bg-primary text-primary-foreground hover:scale-[1.03] active:scale-95 transition-transform shadow-lg flex items-center justify-center"
                    title={builderLocked ? "Sense AI Builder — upgrade to unlock" : "Open Sense AI Builder"}
                    data-testid={builderLocked ? "sense-ai-builder-fab-locked" : "sense-ai-builder-fab"}>
                    <Sparkles className="h-5 w-5" />
                </button>
            </div>
            <button
                onClick={() => setDismissed(true)}
                className="absolute -top-1 -left-1 h-4 w-4 rounded-full bg-secondary text-muted-foreground hover:text-foreground border border-border flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity pointer-events-auto"
                title="Hide for this session"
                data-testid="sense-ai-builder-fab-dismiss">
                <X className="h-2.5 w-2.5" />
            </button>
        </div>
    );
}

